import { ChangeEvent, Dispatch, Fragment, SetStateAction, useEffect, useState } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormGroup,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  SxProps
} from "@mui/material";

import conf from "../../conf";
import format from "../utils/Role";
import Spinner from "../utils/Spinner";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import { IDP, ROLES, STATUS, User } from "../auth/AuthProvider";
import {
  Organization,
  useConfgSysOrganizationUsersEditIdp,
  useConfgSysOrganizationUsersEditRole,
  useConfgSysOrganizationUsersEditStatus,
  useConfgSysOrganizationUsersRemove
} from "../../hooks/confg/base";
import { useVaultInviteCancel, useVaultInviteResend } from "../../hooks/vault/base";

export type ActionType = "assign" | "unassign";

type EditRoleProps = {
  user: User;
  current: ROLES | "";
  organization: Organization;
};

type EditAccountProps = {
  user: User;
  current: STATUS | "";
  organization: Organization;
};

type RemoveAccountProps = {
  user: User;
  organization: Organization;
  sx?: SxProps;
};

type ActionToggleGroupProps = {
  action: ActionType;
  setAction: Dispatch<SetStateAction<ActionType>>;
};

export const EditRole = (props: EditRoleProps) => {
  const current = props.current as ROLES;
  const organizationId = props.organization.id;
  const { id, email } = props.user;

  const [role, setRole] = useState(current);
  const [editing, setEditing] = useState(false);

  const edit = useConfgSysOrganizationUsersEditRole();

  const warning = (
    <>
      You are about to change <b>{email}</b>'s role from <b>{format(current)}</b> to <b>{format(role)}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  const handleCancel = () => {
    setRole(current);
    setEditing(!editing);
  };

  const handleConfirm = () => edit.mutate({ id, role, organizationId });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRole((e.target as HTMLInputElement).value as ROLES);
  };

  useEffect(() => {
    if (edit.isSuccess) setEditing(false);
  }, [edit.isSuccess]);

  return (
    <Stack>
      <FormControl>
        <RadioGroup row name="role-toggle" value={role} onChange={handleChange} aria-labelledby="role-toggle-label">
          {conf.ROLES.map((role) => (
            <FormControlLabel key={role} value={role} label={format(role)} control={<Radio />} disabled={!editing} />
          ))}
        </RadioGroup>
      </FormControl>

      <DialogActions sx={{ px: 0 }}>
        <Button variant="outlined" onClick={handleCancel}>
          {editing ? "Cancel" : "Edit"}
        </Button>

        <ButtonWithConfirm
          type="Edit role"
          label="Submit"
          color="primary"
          status={edit.status}
          message={warning}
          disabled={!editing}
          onConfirm={handleConfirm}
        />
      </DialogActions>
    </Stack>
  );
};

export const EditAccount = (props: EditAccountProps) => {
  const current = props.current as STATUS;
  const organizationId = props.organization.id;
  const { id, email } = props.user;

  const [status, setStatus] = useState(current);
  const [editing, setEditing] = useState(false);

  const edit = useConfgSysOrganizationUsersEditStatus();

  const resend = useVaultInviteResend();
  const cancel = useVaultInviteCancel();

  const warning = (
    <>
      You are about to change <b>{email}</b>'s account access to <b>{status.toUpperCase()}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  const handleReset = () => {
    setStatus(current);
    setEditing(!editing);
  };

  const handleResend = () => resend.mutate(id);
  const handleCancel = () => cancel.mutate(id);
  const handleConfirm = () => edit.mutate({ id, status, organizationId });

  const handleChange = (e: SelectChangeEvent) => {
    setStatus(e.target.value as STATUS);
  };

  useEffect(() => {
    if (edit.isSuccess) setEditing(false);
  }, [edit.isSuccess]);

  return (
    <DialogActions sx={{ px: 0 }}>
      <FormControl fullWidth>
        <Select
          id="status-select"
          labelId="status-select-label"
          size="small"
          value={status}
          disabled={current === "invited"}
          onChange={handleChange}
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="invited" disabled>
            Invited
          </MenuItem>
          <MenuItem value="pending" disabled>
            Pending
          </MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
          <MenuItem value="banned">Banned</MenuItem>
        </Select>
      </FormControl>

      {current === "invited" ? (
        <Fragment>
          <Button fullWidth variant="contained" onClick={handleResend}>
            {resend.isPending ? <Spinner /> : "Resend invite"}
          </Button>

          <Button fullWidth color="error" variant="contained" onClick={handleCancel}>
            {cancel.isPending ? <Spinner /> : "Cancel invite"}
          </Button>
        </Fragment>
      ) : (
        <Fragment>
          <Button variant="outlined" onClick={handleReset} disabled={current === status}>
            Reset
          </Button>

          <ButtonWithConfirm
            type="Edit account"
            label="Submit"
            color="primary"
            status={edit.status}
            message={warning}
            disabled={current === status}
            onConfirm={handleConfirm}
          />
        </Fragment>
      )}
    </DialogActions>
  );
};

export const EditProvider = ({ label = "Identity provider", user }: { user: User; label?: string; disabled?: boolean }) => {
  const [idp, setIdp] = useState<User["idp"]>(user.idp);
  const [editing, setEditing] = useState(false);

  const edit = useConfgSysOrganizationUsersEditIdp();

  const warning = (
    <>
      You are about to change <b>{user.email}</b>'s identity provider. This action cannot be undone. Are you sure you want to
      continue?
    </>
  );

  const handleReset = () => {
    setIdp(user.idp);
    setEditing(!editing);
  };

  const handleConfirm = () => edit.mutate({ id: user.id, idp });

  const onChange = (e: React.MouseEvent<unknown>, value: IDP) => {
    const current = idp.indexOf(value);
    let edited: IDP[] = [];

    if (current === -1) {
      edited = edited.concat(idp, value);
    } else if (current === 0) {
      edited = edited.concat(idp.slice(1));
    } else if (current === idp.length - 1) {
      edited = edited.concat(idp.slice(0, -1));
    } else if (current > 0) {
      edited = edited.concat(idp.slice(0, current), idp.slice(current + 1));
    }

    setIdp(edited);
  };

  useEffect(() => {
    if (edit.isSuccess) setEditing(false);
  }, [edit.isSuccess]);

  return (
    <Box sx={{ minWidth: 120 }}>
      {/* <InputLabel id="idp-select">{label}</InputLabel> */}

      <FormGroup id="idp-select" row>
        {conf.IDPS.map((props) => (
          <FormControlLabel
            key={props.value}
            label={props.label}
            disabled={props.disabled || !editing}
            control={
              <Checkbox checked={idp.includes(props.value as IDP)} onClick={(e) => onChange(e, props.value as IDP)} />
            }
          />
        ))}
      </FormGroup>

      <DialogActions sx={{ px: 0 }}>
        <Button variant="outlined" onClick={handleReset}>
          {editing ? "Cancel" : "Edit"}
        </Button>

        <ButtonWithConfirm
          type="Edit idp"
          label="Submit"
          color="primary"
          status={edit.status}
          message={warning}
          disabled={!editing}
          onConfirm={handleConfirm}
        />
      </DialogActions>
    </Box>
  );
};

export const EditAssignment = (props: ActionToggleGroupProps) => {
  const { action, setAction } = props;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAction(e.target.value as ActionType);
  };

  return (
    <RadioGroup row value={action} onChange={handleChange} aria-label="assignment-type">
      <FormControlLabel value="assign" label="assign" control={<Radio size="small" color="default" />} />
      <FormControlLabel value="unassign" label="unassign" control={<Radio size="small" color="default" />} />
    </RadioGroup>
  );
};

export const RemoveAccount = (props: RemoveAccountProps) => {
  const organizationId = props.organization.id;
  const { id, email } = props.user;

  const remove = useConfgSysOrganizationUsersRemove();

  const handleRemove = () => remove.mutate({ organizationId, userIds: [id], comment: "" });

  const warning = (
    <>
      You are about to remove <b>{email}</b> from <b>{props.organization.name}</b>.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  return (
    <ButtonWithConfirm
      fullWidth
      type="Remove Account"
      label="Remove Account"
      color="error"
      variant="outlined"
      status={remove.status}
      message={warning}
      onConfirm={handleRemove}
      isLoading={remove.isPending}
      sx={props.sx}
    />
  );
};
